import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, MailWarning, X, BadgeCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useMember, formatApiError } from "../lib/memberAuth";
import { MemberCard } from "../components/account/MemberCard";
import { GivingSection } from "../components/account/GivingSection";
import { PurchasesSection, TicketsSection } from "../components/account/PurchasesSection";
import { MinistriesSection, statusLabel } from "../components/account/MinistriesSection";
import { ProfileSection } from "../components/account/ProfileSection";
import { SecuritySection } from "../components/account/SecuritySection";

const completion = (m) => {
  const fields = [m.name, m.email, m.phone, m.address, m.birthday, m.photo, m.preferred_name];
  return Math.round((fields.filter(Boolean).length / fields.length) * 100);
};

const TABS = [
  ["overview", "My Card"], ["giving", "My Giving"], ["purchases", "My Purchases"],
  ["tickets", "My Tickets & Events"], ["ministries", "My Ministries & Groups"], ["profile", "My Profile"],
];
const inp = "w-full border border-black/15 rounded-xl px-4 py-3 text-sm focus:border-[#E63946] outline-none";
const L = ({ children }) => <label className="block text-xs uppercase tracking-[0.18em] text-black/50 mb-2">{children}</label>;

const VerifyBanner = ({ member, setMember }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/members/verify-email", { code });
      setMember({ ...member, email_verified: true });
      toast.success("Email verified! Your records are now linked.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const resend = async () => { try { await api.post("/members/resend-code"); toast.success("New code sent to your email"); } catch { toast.error("Failed to resend"); } };
  return (
    <div className="bg-[#E63946]/5 border border-[#E63946]/25 rounded-2xl p-5 mb-8" data-testid="verify-banner">
      <p className="font-medium flex items-center gap-2 text-sm"><MailWarning size={16} className="text-[#E63946]" /> Verify your email to link your giving, tickets and orders</p>
      <p className="text-xs text-black/50 mt-1">We sent a 6-digit code to {member.email}.</p>
      <div className="flex gap-2 mt-3 max-w-sm">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="verify-code-input"
          className="flex-1 border border-black/15 rounded-full px-4 py-2.5 text-sm tracking-[0.3em] font-mono focus:border-[#E63946] outline-none" />
        <button onClick={verify} disabled={busy || code.length < 6} data-testid="verify-submit-btn"
          className="rounded-full bg-[#E63946] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#BA1B25] disabled:opacity-50">Verify</button>
      </div>
      <button onClick={resend} className="text-xs text-black/50 underline mt-2">Resend code</button>
    </div>
  );
};

const BecomeMember = ({ member }) => {
  const [app, setApp] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ phone: member.phone || "", address: member.address || "", how_long: "", previous_church: "", baptized: "", serve_areas: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/members/membership-application").then((r) => setApp(r.data)).catch(() => setApp(null)); }, []);
  if (app === undefined) return null;
  if (member.membership_status === "Active Member") return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/members/membership/apply", f);
      setApp(data); setOpen(false);
      toast.success("Membership application submitted!");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="mt-6" data-testid="become-member-block">
      {app ? (
        <div className="bg-white border border-black/10 rounded-2xl p-5 flex items-center gap-3">
          <BadgeCheck size={20} className="text-[#E63946]" />
          <div>
            <p className="font-medium text-sm">Membership Application — {statusLabel(app.status)}</p>
            <p className="text-xs text-black/45">Submitted {new Date(app.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="become-member-btn"
          className="rounded-full bg-[#0A0A0A] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#E63946] transition-colors">Become a Member</button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md my-8" onClick={(e) => e.stopPropagation()} data-testid="membership-apply-modal">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <h3 className="font-display font-bold text-lg">Become a Member</h3>
              <button onClick={() => setOpen(false)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-black/5"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
              <div><L>Phone</L><input className={inp} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><L>Address</L><input className={inp} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
              <div><L>How long have you attended One Love?</L><input className={inp} value={f.how_long} onChange={(e) => setF({ ...f, how_long: e.target.value })} data-testid="membership-howlong-input" /></div>
              <div><L>Previous church (if any)</L><input className={inp} value={f.previous_church} onChange={(e) => setF({ ...f, previous_church: e.target.value })} /></div>
              <div><L>Have you been baptized?</L><input className={inp} value={f.baptized} onChange={(e) => setF({ ...f, baptized: e.target.value })} /></div>
              <div><L>Where would you like to serve?</L><input className={inp} value={f.serve_areas} onChange={(e) => setF({ ...f, serve_areas: e.target.value })} /></div>
              <button onClick={submit} disabled={busy} data-testid="submit-membership-btn"
                className="w-full rounded-full bg-[#E63946] text-white py-3.5 font-medium hover:bg-[#BA1B25] disabled:opacity-60">{busy ? "Submitting…" : "Submit Application"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Account() {
  const { member, setMember, logout } = useMember();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const [card, setCard] = useState(null);
  const [welcome, setWelcome] = useState(params.get("welcome") === "1");

  useEffect(() => { if (member === false) navigate("/account/login"); }, [member, navigate]);
  useEffect(() => { if (member) api.get("/members/card").then((r) => setCard(r.data)).catch(() => {}); }, [member]);

  if (member === null) return <div className="pt-[72px] min-h-screen grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" size={32} /></div>;
  if (!member) return null;

  return (
    <div className="pt-[72px] min-h-screen bg-[#FAFAFA]" data-testid="account-page">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Welcome, {member.preferred_name || member.name?.split(" ")[0]}</h1>
            <p className="text-black/50 text-sm mt-1">Your One Love Ministries Member Portal</p>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="member-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm hover:bg-black/5"><LogOut size={15} /> Sign Out</button>
        </div>

        {!member.email_verified && <VerifyBanner member={member} setMember={setMember} />}

        <div className="flex gap-2 overflow-x-auto pb-2 mb-8">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`account-tab-${v}`}
              className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm transition-colors ${tab === v ? "bg-[#0A0A0A] text-white" : "border border-black/15 text-black/60 hover:border-black/40"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div>
            <MemberCard card={card} />
            <BecomeMember member={member} />
            <p className="text-xs text-black/40 mt-6 max-w-xl">Your digital membership card works for church and event check-in. Your status updates automatically when the church office updates your membership.</p>
          </div>
        )}
        {tab === "giving" && <GivingSection />}
        {tab === "purchases" && <PurchasesSection />}
        {tab === "tickets" && <TicketsSection />}
        {tab === "ministries" && <MinistriesSection />}
        {tab === "profile" && <><ProfileSection /><SecuritySection /></>}
      </div>

      {welcome && member && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setWelcome(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="welcome-modal">
            <Sparkles className="text-[#E63946] mx-auto mb-3" size={36} />
            <h2 className="font-display font-extrabold text-2xl">Welcome to One Love Ministries{member.name ? `, ${member.name.split(" ")[0]}` : ""}!</h2>
            <p className="text-black/55 text-sm mt-2">Your account is ready. From here you can view your giving, access purchases and tickets, join ministries, apply for membership, and use your Digital Membership Card.</p>
            <div className="mt-5">
              <div className="flex justify-between text-xs text-black/45 mb-1.5"><span>Profile Completion</span><span className="font-bold text-black">{completion(member)}%</span></div>
              <div className="h-2 rounded-full bg-black/10 overflow-hidden"><div className="h-full bg-[#E63946] rounded-full transition-all" style={{ width: `${completion(member)}%` }} /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setWelcome(false); window.history.replaceState(null, "", "/account"); }} data-testid="welcome-later-btn"
                className="flex-1 rounded-full border border-black/15 py-3 text-sm hover:bg-black/5">Do This Later</button>
              <button onClick={() => { setWelcome(false); setTab("profile"); window.history.replaceState(null, "", "/account"); }} data-testid="welcome-complete-btn"
                className="flex-1 rounded-full bg-[#E63946] text-white py-3 text-sm font-medium hover:bg-[#BA1B25]">Complete My Profile</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
